import { FC } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  FormGroup,
  IconButton,
  styled,
  TextareaAutosize,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import { useDispatch, useSelector } from '../../../../shared/hooks/redux';
import { PageUnauthenticated } from '../../../../shared/components/page';
import { Header } from '../../../../components/header';
import { Content } from '../../../../shared/components/Content';
import { InnsendingLinje } from '../../../../components/Header4';
import { CancelButton } from '../../../../shared/components/CancelButton';
import {
  resetStates,
  setTypeOfDeclaration,
  setSortedAndControlled,
  setMeetsRequirements,
  setHasDocumentationOfCleanliness,
  setReasonForNoDocumentationOfCleanliness,
  setDeclaration,
  setDocumentationOfCleanliness,
} from '../../../../core/redux/massOrderState';
import { RadioGroup } from '../../../../shared/components/radioGroup';
import { RadioYesNo } from '../../../../components/RadioYesNo';

import { useDeleteFileMutation, useUploadFilesMutation } from '../../../../core/redux/transport';
import { FileLink } from '../../../../shared/components/FileLink';
import { UploadedFile } from '../../../../core/types/transport/api/uploadFilesResponse';

const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

export const UploadButton: FC<{
  text: string;
  onFileChange: (files: FileList | null) => void;
  multiple?: boolean;
  loading?: boolean;
}> = ({
  text, onFileChange, multiple = false, loading = false,
}) => (
  <div className="page-item">
    <Button
      component="label"
      role={undefined}
      variant="contained"
      tabIndex={-1}
      disabled={loading}
      startIcon={loading ? <CircularProgress size={24} /> : <CloudUploadIcon />}
    >
      {text}
      <VisuallyHiddenInput
        type="file"
        onChange={(e) => onFileChange(e.target.files)}
        multiple={multiple}
      />
    </Button>
  </div>
);

export const EgenerklaeringPage: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    massType,
    typeOfDeclaration,
    sortedAndControlled,
    meetsRequirements,
    hasDocumentationOfCleanliness,
    reasonForNoDocumentationOfCleanliness,
    uploadedFileDeclaration,
    documentationOfCleanliness,
  } = useSelector((s) => s.massOrderState);

  const disableNextPage = typeOfDeclaration === undefined
    || (typeOfDeclaration === 'FillOutNow' && sortedAndControlled === undefined)
    || (typeOfDeclaration === 'FillOutNow' && meetsRequirements === undefined)
    || (typeOfDeclaration === 'FillOutNow'
      && hasDocumentationOfCleanliness === undefined)
    || (typeOfDeclaration === 'FillOutNow'
      && !hasDocumentationOfCleanliness
      && (reasonForNoDocumentationOfCleanliness === undefined
        || reasonForNoDocumentationOfCleanliness === ''))
    || (typeOfDeclaration === 'FillOutNow'
      && hasDocumentationOfCleanliness
      && (documentationOfCleanliness === undefined
        || documentationOfCleanliness.length === 0))
    || (typeOfDeclaration === 'UploadNow' && !uploadedFileDeclaration);

  const [uploadDeclaration, { isLoading: isUploadingDeclaration }] = useUploadFilesMutation();
  const [deleteFile, { isLoading: isDeletingFile }] = useDeleteFileMutation();
  const [
    uploadDocumentationOfCleanliness,
    { isLoading: isUploadingDocumentation },
  ] = useUploadFilesMutation();

  const onUploadDeclaration = (file: FileList | null) => {
    if (file && file.length === 1) {
      uploadDeclaration(file)
        .unwrap()
        .then((uploadedFiles) => {
          if (uploadDeclaration.length > 0) {
            dispatch(setDeclaration(uploadedFiles[0]));
          }
        });
    }
  };

  const onUploadDocumentationOfCleanliness = (files: FileList | null) => {
    if (files) {
      uploadDocumentationOfCleanliness(files)
        .unwrap()
        .then((uploadedFiles) => {
          const newFileList = documentationOfCleanliness
            ? [...documentationOfCleanliness, ...uploadedFiles]
            : uploadedFiles;
          dispatch(setDocumentationOfCleanliness(newFileList));
        });
    }
  };

  const handleDeleteCleanlinessDocument = async (file: UploadedFile) => {
    await deleteFile(file.id).unwrap();
    dispatch(
      setDocumentationOfCleanliness(
        documentationOfCleanliness?.filter((f) => f.id !== file.id) || [],
      ),
    );
  };

  const handleDeleteDeclaration = async (file: UploadedFile) => {
    await deleteFile(file.id).unwrap();
    dispatch(setDeclaration(undefined));
  };

  return (
    <PageUnauthenticated className="egenerklaering-page parent-pos">
      <Header>Egenerklæring</Header>
      <Content>
        <InnsendingLinje text="Steg 2 - Egenerklæring" styling="page-item" />

        <div className="page-item">
          <InnsendingLinje text="Massene skal kunne dokumenteres som rene. Finnes det egenerklæring?" />
          <RadioGroup
            onChange={(value) => dispatch(setTypeOfDeclaration(value))}
            options={[
              { id: 'UploadNow', label: 'Ja, jeg vil laste den opp nå.' },
              {
                id: 'SentEarlier',
                label: 'Ja, den er lastet opp/send tidligere.',
              },
              { id: 'WillSendLater', label: 'Nei, den vil bli ettersendt.' },
              { id: 'FillOutNow', label: 'Nei, jeg vil fylle den ut nå.' },
            ]}
            value={typeOfDeclaration}
          />
        </div>

        {typeOfDeclaration === 'UploadNow' && (
          <>
            <div className="page-item vertical-padding">
              <UploadButton
                loading={isUploadingDeclaration}
                text="Last opp egenerklæring"
                onFileChange={onUploadDeclaration}
              />
            </div>
            <div className="page-item vertical-padding">
              {uploadedFileDeclaration && (
                <Box display="flex" gap={2} alignItems="center">
                  <span>
                    Lastet opp fil: <FileLink file={uploadedFileDeclaration} />
                  </span>
                  <IconButton
                    onClick={() => handleDeleteDeclaration(uploadedFileDeclaration)}
                    disabled={isDeletingFile}
                    size="small"
                    color="primary"
                  >
                    <DeleteIcon fontSize="inherit" />
                  </IconButton>
                </Box>
              )}
            </div>
          </>
        )}

        {typeOfDeclaration === 'FillOutNow' && (
          <>
            <InnsendingLinje text="Egenerklæring for masse" />
            <div className="page-item">
              <FormGroup>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={sortedAndControlled ?? false}
                      onChange={(event) => dispatch(setSortedAndControlled(event.target.checked))}
                    />
                  )}
                  label="Jeg bekrefter at det er foretatt en grundig sortering og kontroll av massepartiet."
                />
              </FormGroup>
            </div>
            <div className="page-item">
              <FormGroup>
                <FormControlLabel
                  control={(
                    <Checkbox
                      checked={meetsRequirements ?? false}
                      onChange={(event) => dispatch(setMeetsRequirements(event.target.checked))}
                    />
                  )}
                  label={(
                    <>
                      Jeg bekrefter at massen tilfredsstiller krav i prosedyre{' '}
                      {massType?.procedureName} &nbsp;
                      <a
                        href={massType?.procedureUrl}
                        target="_blank"
                        rel="noreferrer"
                      >
                        (Lenke til prosedyre)
                      </a>
                    </>
                  )}
                />
              </FormGroup>
            </div>
            <InnsendingLinje text="Har dere dokumentasjon på at massene er rene?" />
            <div className="page-item">
              <RadioYesNo
                value={hasDocumentationOfCleanliness}
                onChange={(v) => dispatch(setHasDocumentationOfCleanliness(v))}
              />
            </div>
          </>
        )}

        {typeOfDeclaration === 'FillOutNow'
          && hasDocumentationOfCleanliness === true && (
            <>
              <InnsendingLinje text="Legg ved saneringsbeskrivelse og/eller grunnundersøkelser" />
              <div className="text-item flex-parent vertical-padding">
                <UploadButton
                  text="Last opp dokumentasjon"
                  onFileChange={onUploadDocumentationOfCleanliness}
                  multiple
                  loading={isUploadingDocumentation}
                />
              </div>

              {documentationOfCleanliness
                && documentationOfCleanliness.length > 0 && (
                  <div className="page-item vertical-padding">
                    <p>Lastet opp fil(er):</p>
                    <ul>
                      {Array.from(documentationOfCleanliness).map((file) => (
                        <li key={file.id}>
                          <Box display="flex" gap={2} alignItems="center">
                            <FileLink file={file} />
                            <IconButton
                              onClick={() => handleDeleteCleanlinessDocument(file)}
                              disabled={isDeletingFile}
                              size="small"
                              color="primary"
                            >
                              <DeleteIcon fontSize="inherit" />
                            </IconButton>
                          </Box>
                        </li>
                      ))}
                    </ul>
                  </div>
              )}
            </>
        )}

        {typeOfDeclaration === 'FillOutNow'
          && hasDocumentationOfCleanliness === false && (
            <>
              <InnsendingLinje text="Oppgi årsak til at dere ikke har dokumentasjon på at massene er rene" />
              <div className="text-item flex-parent">
                <TextareaAutosize
                  minRows={10}
                  placeholder="Fyll ut årsak"
                  value={reasonForNoDocumentationOfCleanliness}
                  className="fill-screen"
                  onChange={(e) => dispatch(
                    setReasonForNoDocumentationOfCleanliness(e.target.value),
                  )}
                />
              </div>
            </>
        )}

        <div className="bottom-inner">
          <div className="bottom-element">
            <Button
              className="left-element"
              onClick={() => navigate(-1)}
              variant="contained"
              size="large"
            >
              {'<'}
            </Button>
            <CancelButton
              confirm={() => {
                dispatch(resetStates());
                return navigate('/bestilling');
              }}
            />
            <Button
              className="right-element"
              onClick={() => navigate('/masseut/leveringsdetaljer')}
              variant="contained"
              size="large"
              disabled={disableNextPage}
            >
              {'>'}
            </Button>
          </div>
        </div>
      </Content>
    </PageUnauthenticated>
  );
};
